/**
 * Announcements 패널 — Overview 우측 정보 패널의 공지 칸.
 *
 * 읽는 사람이 **언제든 찾아오는 자리**다. 배너(AnnouncementBanner)는 안 읽은
 * 것이 있을 때만 잠깐 뜨고 읽으면 사라지므로, 그것만으로는 지난 공지를 다시
 * 볼 길이 없다 — 이 패널이 그 자리를 맡는다.
 *
 * 관례는 Recent Activity·Tutorial 패널과 같다(테두리 Paper + 머리말 + dense 목록).
 */

import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Chip,
  Divider,
  List,
  ListItemButton,
  ListItemText,
  Paper,
  Skeleton,
  Typography,
} from '@mui/material';
import { formatDateTime } from '@/shared/dateTime';
import { COMPOSE_PATH, isAnnounced } from '@/app/services/announcement/announcementService';
import { useAuthStore } from '@/app/stores/authStore';
import { useAnnouncementStore } from '@/app/stores/announcementStore';

/** 패널에 직접 노출할 공지 수 (나머지는 "전체 공지 보기"로) */
const PREVIEW_LIMIT = 4;

const AnnouncementsPanel: React.FC = () => {
  const navigate = useNavigate();
  // 읽음 상태의 주인은 스토어 하나 — 같은 화면의 배너와 어긋나지 않는다
  const items = useAnnouncementStore((s) => s.items);
  const lastRead = useAnnouncementStore((s) => s.lastReadNumber);
  const unread = useAnnouncementStore((s) => s.unreadCount);
  const loaded = useAnnouncementStore((s) => s.loaded);
  const failed = useAnnouncementStore((s) => s.failed);
  const load = useAnnouncementStore((s) => s.load);
  const isAdmin = useAuthStore((s) => s.profile?.is_admin ?? false);

  useEffect(() => {
    void load();
  }, [load]);

  // 초안은 이 칸에 오지 않는다 — 알린 것만 읽는 사람의 자리다.
  // 거르고 나면 서버가 준 차례가 곧 번호 내림차순이라 최신이 위에 온다.
  const announced = items.filter(isAnnounced);

  const shown = announced.slice(0, PREVIEW_LIMIT);

  return (
    <Paper
      elevation={0}
      sx={{ border: 1, borderColor: 'divider', borderRadius: 2, overflow: 'hidden' }}
      data-testid="announcements-panel"
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          px: 2,
          py: 1.5,
          borderBottom: 1,
          borderColor: 'divider',
        }}
      >
        <Typography variant="subtitle1" fontWeight={600}>
          공지
        </Typography>
        {unread > 0 && (
          <Chip
            label={unread}
            size="small"
            color="primary"
            sx={{ height: 20, fontSize: '0.7rem' }}
          />
        )}
      </Box>

      {!loaded ? (
        <Box sx={{ px: 2, py: 1 }}>
          {Array.from({ length: 3 }, (_, i) => (
            <Skeleton key={i} height={36} />
          ))}
        </Box>
      ) : shown.length === 0 ? (
        <Box sx={{ px: 2, py: 2 }}>
          <Typography variant="caption" color="text.secondary">
            {failed
              ? '공지를 불러오지 못했습니다 — 잠시 후 다시 시도해 주세요.'
              : '아직 공지가 없습니다.'}
          </Typography>
          {/* 0건이면 아래 [전체 공지 보기]가 없다 — 관리자가 첫 공지를 쓸 길이
              여기 말고는 없으므로 이 자리에서 바로 쓰게 한다. 못 받은 것뿐일
              때는 내밀지 않는다(없는 것이 아니라 모르는 것이다). */}
          {!failed && isAdmin && (
            <Box sx={{ mt: 1 }}>
              <Button size="small" variant="outlined" onClick={() => navigate(COMPOSE_PATH)}>
                공지 작성
              </Button>
            </Box>
          )}
        </Box>
      ) : (
        <List dense sx={{ py: 0 }}>
          {shown.map((a, i) => (
            <React.Fragment key={a.id}>
              <ListItemButton
                component="li"
                sx={{ py: 1, pr: 1, minWidth: 0 }}
                onClick={() => navigate(`/announcements/${a.number}`)}
              >
                <ListItemText
                  primary={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, minWidth: 0 }}>
                      {/* 안 읽은 것에만 점 — 읽은 것과 한눈에 갈린다 */}
                      {a.number > lastRead && (
                        <Box
                          data-testid="announcement-unread-dot"
                          sx={{
                            width: 6,
                            height: 6,
                            borderRadius: '50%',
                            bgcolor: 'primary.main',
                            flexShrink: 0,
                          }}
                        />
                      )}
                      <Typography
                        variant="body2"
                        noWrap
                        fontWeight={a.number > lastRead ? 600 : 400}
                        sx={{ minWidth: 0 }}
                      >
                        {a.title}
                      </Typography>
                    </Box>
                  }
                  secondary={
                    <Typography variant="caption" color="text.secondary">
                      {a.authorName || '운영자'} · {formatDateTime(a.publishedAt ?? a.createdAt)}
                    </Typography>
                  }
                />
              </ListItemButton>
              {i < shown.length - 1 && <Divider component="li" />}
            </React.Fragment>
          ))}
        </List>
      )}

      {announced.length > 0 && (
        <>
          <Divider />
          <ListItemButton sx={{ py: 1 }} onClick={() => navigate('/announcements')}>
            <ListItemText
              primary={
                <Typography variant="caption" color="primary">
                  전체 공지 보기
                </Typography>
              }
            />
          </ListItemButton>
        </>
      )}
    </Paper>
  );
};

export default AnnouncementsPanel;
